import { type CSSProperties, type ReactNode, useCallback, useState } from "react";

import { Curtain } from "./components/Curtain";
import { PersonaChat } from "./components/PersonaChat";
import { ThemeToggle } from "./components/ThemeToggle";
import { useReveal } from "./hooks/useReveal";
import { isAbsoluteHttpUrl } from "./lib/persona-client";

const PORTFOLIO_URL = "https://www.irfansyafi.com/";
const LINKEDIN_URL = "https://www.linkedin.com/in/irfan-syafi/";

/** Sets the per-element stagger step read by `.reveal` and `.curtain__letter`. */
const step = (index: number) => ({ "--i": index }) as CSSProperties;

export function App() {
  // Truthiness is not enough. A VITE_PERSONA_API_URL without a scheme
  // ("ai-api.irfansyafi.com") is a *relative* URL: the client would resolve it
  // against this page's own origin and POST into the static host. Treat a
  // malformed value exactly like a missing one, so a bad deployment shows the
  // setup notice rather than throwing out of render and blanking the page.
  const apiUrl = import.meta.env.VITE_PERSONA_API_URL;
  const configured = isAbsoluteHttpUrl(apiUrl);

  // The hero cascade starts when the curtain *begins* lifting, so the two
  // overlap into one movement instead of queueing.
  const [ready, setReady] = useState(false);
  const onCurtainDone = useCallback(() => setReady(true), []);

  const chat = useReveal<HTMLDivElement>();

  // Hero elements share one reveal state and differ only by their --i step.
  const enter = ready ? "reveal is-in" : "reveal";

  return (
    <>
      <Curtain onDone={onCurtainDone} />

      {/* Mirrors the portfolio's navigation: wordmark left, theme switch right. */}
      <header className="mx-auto flex max-w-5xl items-center justify-between px-6 py-6">
        <a
          href={PORTFOLIO_URL}
          className={`${enter} text-lg font-semibold text-title transition-colors duration-150 hover:text-primary`}
          style={step(0)}
        >
          Irfan
        </a>
        <div className={enter} style={step(1)}>
          <ThemeToggle />
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 pb-24">
        {/* ------------------------------------------------------------------
            Hero — the portfolio's two-column composition: a vertical social
            rail and the text on the left, a circular portrait on the right.
            ------------------------------------------------------------------ */}
        <section className="grid items-center gap-10 py-10 md:grid-cols-[1fr_auto] md:gap-16 md:py-16">
          <div className="flex flex-row items-start gap-6 md:gap-8">
            {/* The site runs its social links as a vertical rail beside the
                intro rather than under it. */}
            <ul
              className={`${enter} order-2 flex flex-row gap-5 md:order-1 md:flex-col`}
              style={step(5)}
            >
              <li>
                <SocialLink href={LINKEDIN_URL} label="LinkedIn">
                  <LinkedInIcon />
                </SocialLink>
              </li>
              <li>
                <SocialLink href={PORTFOLIO_URL} label="Portfolio">
                  <GlobeIcon />
                </SocialLink>
              </li>
            </ul>

            <div className="order-1 md:order-2">
              <h1
                className={`${enter} text-4xl font-semibold text-title md:text-5xl`}
                style={step(0)}
              >
                Hi, I am Irfan
              </h1>
              <h3
                className={`${enter} mt-1 mb-3 text-lg font-medium text-text`}
                style={step(1)}
              >
                AI persona
              </h3>
              <p className={`${enter} max-w-xl leading-relaxed`} style={step(2)}>
                Not the real one &mdash; a model answering from what he has written about
                his work in artificial intelligence, big data and cloud. It speaks in his
                voice, and it will tell you plainly when a question is outside what it
                knows.
              </p>
              <p className={`${enter} mt-5 text-sm text-text-light`} style={step(3)}>
                For the real Irfan, the{" "}
                <a
                  href={PORTFOLIO_URL}
                  className="text-primary underline underline-offset-2"
                >
                  portfolio
                </a>{" "}
                is next door.
              </p>
            </div>
          </div>

          {/* Portrait slot. The site floats a soft shape behind a circular
              photo; here the shape stays and the circle carries a monogram,
              because inventing a photograph of a real person is not on. */}
          <div
            className={`${enter} relative mx-auto h-56 w-56 md:mx-0 md:h-64 md:w-64`}
            style={step(4)}
            aria-hidden="true"
          >
            <div className="sway absolute inset-0">
              <div className="h-full w-full rounded-[42%_58%_54%_46%/46%_44%_56%_54%] bg-primary/15 blur-2xl" />
            </div>
            {/* Initials, not a single letter: a lone "I" in Inter is a bare
                vertical stroke and reads as a rendering glitch rather than a
                monogram. */}
            <div className="absolute inset-5 grid place-items-center rounded-full border border-primary/30 bg-surface/60">
              <span className="text-5xl font-semibold tracking-tight text-primary">
                IS
              </span>
            </div>
          </div>
        </section>

        {/* ------------------------------------------------------------------
            Chat — presented with the portfolio's section pattern: a centred
            heading over a small muted subtitle.
            ------------------------------------------------------------------ */}
        <section className="pt-6">
          <div
            ref={chat.ref}
            className={`reveal ${chat.shown ? "is-in" : ""}`}
            style={step(0)}
          >
            <h2 className="text-center text-3xl font-semibold text-title">Ask me</h2>
            <p className="mt-1 text-center text-sm text-text-light">
              A conversation with the digital version of me
            </p>

            <div className="mt-8">{configured ? <PersonaChat /> : <SetupNotice value={apiUrl} />}</div>

            <p className="mt-6 text-center text-xs text-text-light">
              Replies are generated and can be wrong. For anything that matters,{" "}
              <a
                href={PORTFOLIO_URL}
                className="text-primary underline underline-offset-2"
              >
                reach me directly
              </a>
              .
            </p>
          </div>
        </section>
      </main>
    </>
  );
}

function SocialLink({
  href,
  label,
  children,
}: {
  href: string;
  label: string;
  children: ReactNode;
}) {
  return (
    <a
      href={href}
      aria-label={label}
      target="_blank"
      rel="noreferrer"
      // The site lifts its social icons on hover with a 0.3s transform.
      className="inline-block text-primary transition-transform duration-300 hover:-translate-y-1"
    >
      {children}
    </a>
  );
}

/**
 * Shown when the API URL is missing or malformed.
 *
 * A configuration mistake is the most likely first-run failure, so it gets a
 * real explanation and the exact command to fix it rather than a blank panel or
 * a thrown error in the console. The two cases are worth distinguishing: a
 * missing value is a step not done yet, a scheme-less one is a value that looks
 * right and silently is not.
 */
function SetupNotice({ value }: { value?: string }) {
  return (
    <section className="rounded-app border border-border bg-surface px-6 py-8 text-center">
      <h3 className="text-lg font-semibold text-title">Point this at your Worker</h3>
      <p className="mx-auto mt-2 max-w-md text-sm text-text">
        <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
          VITE_PERSONA_API_URL
        </code>{" "}
        {value
          ? `is "${value}", which has no https:// scheme. Without one the browser treats it as a path on this site instead of another host.`
          : "is not set, so there is no API to talk to."}
      </p>
      <pre className="mx-auto mt-4 w-fit rounded-app bg-muted px-4 py-3 text-left text-xs text-text">
        <code>
          cp .env.example .env.local{"\n"}
          # then set VITE_PERSONA_API_URL and restart
        </code>
      </pre>
    </section>
  );
}

function LinkedInIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M4.98 3.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5ZM3 9h4v12H3V9Zm7 0h3.8v1.7h.05c.53-.95 1.83-1.95 3.77-1.95 4.03 0 4.78 2.5 4.78 5.76V21h-4v-5.7c0-1.36-.03-3.1-1.9-3.1-1.9 0-2.2 1.47-2.2 3v5.8h-4V9Z" />
    </svg>
  );
}

function GlobeIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3a15 15 0 0 1 0 18a15 15 0 0 1 0-18Z" />
    </svg>
  );
}
